import {
  snapshotMonthly,
  type PriceList,
  type SavedImage,
  type Server,
  type ServerType,
} from "@cloudserver/contracts";
import { useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import {
  ArchiveRestore,
  Camera,
  CopyPlus,
  Lock,
  LockOpen,
  Pencil,
  Save,
  Trash2,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../../api/client.js";
import { OsIcon } from "../../ui/os-icon.js";
import {
  useConvertImage,
  useDeleteImage,
  useImageProtection,
  useUpdateImage,
} from "../../api/images.js";
import { useServerAction } from "../../api/servers.js";
import { useReadOnly } from "../../api/users.js";
import { DangerDialog } from "../../ui/confirm.js";
import { ErrorMessage, problemText } from "../../ui/feedback.js";
import { dateTime, money } from "../../ui/format.js";
import { Dialog, type MenuItem } from "../../ui/overlay.js";
import { Alert, Badge, Button, TextArea, TextField } from "../../ui/primitives.js";
import { useToast } from "../../ui/toast.js";
import { formatLabels, parseLabels } from "../labels.js";
import { RootPasswordDialog } from "../server/rescue.js";

/*
 * What the snapshots page and a server's backups tab share: the row menu of
 * an image, its dialogs (take a snapshot, edit, restore a server, keep a
 * backup as a snapshot, delete), and how sizes, prices and states are shown.
 */

export type ImageDialog =
  | { kind: "edit" | "restore" | "convert" | "delete"; image: SavedImage }
  | { kind: "snapshot"; serverId: string | null }
  | null;

/** "2.3 GB" ("2,3 ГБ" in Russian, like the texts around it); null (still being created) as a dash. */
export function gigabytes(value: number | null, locale: string): string {
  if (value === null) return "—";
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: value < 10 ? 2 : 1 });
  return `${number.format(value)} ${locale.startsWith("ru") ? "ГБ" : "GB"}`;
}

/** Monthly price of a snapshot, formatted; a dash while its size is unknown. */
export function snapshotPrice(image: SavedImage, prices: PriceList | undefined, locale: string) {
  if (!prices) return "—";
  const amount = snapshotMonthly(image.sizeGb, prices.snapshotPerGbMonth.net);
  return amount === null ? "—" : money(String(amount), prices.currency, locale, 2);
}

/** Monthly backup price of a server: the backup share of its price in its region. */
export function backupMonthly(
  server: Server,
  types: ServerType[] | undefined,
  prices: PriceList | undefined,
): number | null {
  const type = types?.find((x) => x.code === server.type.code);
  const price = type?.prices.find((p) => p.region === server.region);
  if (!price || !prices) return null;
  return (Number(price.monthly.net) * Number(prices.backupPercent)) / 100;
}

/** "Ubuntu 26.04 · x86". */
export const osLine = (image: SavedImage) =>
  [image.osFlavor, image.osVersion].filter(Boolean).join(" ") + ` · ${image.architecture}`;

/** The OS line under an image's description, with the system's logo. */
export function OsLine({ image }: { image: SavedImage }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted">
      <OsIcon
        flavor={image.osFlavor}
        description={image.description}
        decorative
        className="size-3.5"
      />
      {osLine(image)}
    </span>
  );
}

export function StatusBadge({ image }: { image: SavedImage }) {
  const { t } = useTranslation();
  if (image.status === "available") return null;
  return (
    <Badge tone={image.status === "creating" ? "warn" : "danger"}>
      {t(`images.status.${image.status}`)}
    </Badge>
  );
}

/** Why `server` cannot be restored from `image` now, or null when it can. */
export function restoreBlocker(
  t: TFunction,
  server: Server,
  image: SavedImage,
  busy: boolean,
): string | null {
  if (busy || server.locked) return t("servers.busy");
  if (server.protection.rebuild) return t("errors.reasons.server_rebuild_protected");
  if (server.type.architecture !== image.architecture)
    return t("images.why.architecture", { arch: image.architecture });
  if (server.type.diskGb < image.diskGb) return t("images.why.disk", { disk: image.diskGb });
  return null;
}

/** Toast text of a failed request: the API's reason in plain words. */
export function useFailure() {
  const { t } = useTranslation();
  const toast = useToast();
  return (error: unknown) =>
    toast({
      tone: "danger",
      title:
        error instanceof ApiProblem && error.problem
          ? problemText(t, error.problem)
          : t("common.unexpected"),
    });
}

/** The row menu of an image; blocked actions stay visible with the reason. */
export function useImageMenu(projectId: string, open: (d: ImageDialog) => void) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const failed = useFailure();
  const protection = useImageProtection(projectId);
  const { readOnly, reason: readOnlyReason } = useReadOnly(projectId);

  return (image: SavedImage, busy: boolean): MenuItem[][] => {
    const ready = image.status === "available";
    const notReady = t("images.why.notReady");
    const snapshot = image.kind === "snapshot";
    const blocked = (extra: boolean, reason: string) => ({
      disabled: readOnly || busy || extra,
      reason: readOnly ? readOnlyReason : busy ? t("images.why.busy") : reason,
    });
    return [
      [
        {
          label: t("images.restore"),
          icon: <ArchiveRestore className="size-4" aria-hidden />,
          ...blocked(!ready, notReady),
          onSelect: () => open({ kind: "restore", image }),
        },
        {
          label: t("images.newServer"),
          icon: <CopyPlus className="size-4" aria-hidden />,
          disabled: readOnly || !ready,
          reason: readOnly ? readOnlyReason : notReady,
          onSelect: () =>
            void navigate({
              to: "/projects/$projectId/servers/new",
              params: { projectId },
              search: { snapshot: image.id, imageTab: "own" },
            }),
        },
      ],
      [
        {
          label: t("images.edit"),
          icon: <Pencil className="size-4" aria-hidden />,
          ...blocked(false, ""),
          onSelect: () => open({ kind: "edit", image }),
        },
        snapshot
          ? {
              label: image.protection.delete ? t("images.unprotect") : t("images.protect"),
              icon: image.protection.delete ? (
                <LockOpen className="size-4" aria-hidden />
              ) : (
                <Lock className="size-4" aria-hidden />
              ),
              ...blocked(false, ""),
              onSelect: () =>
                protection.mutate(
                  { id: image.id, protect: !image.protection.delete },
                  {
                    onSuccess: () =>
                      toast({
                        tone: "ok",
                        title: image.protection.delete
                          ? t("images.unprotecting", { name: image.description })
                          : t("images.protecting", { name: image.description }),
                      }),
                    onError: failed,
                  },
                ),
            }
          : {
              label: t("images.convert"),
              icon: <Save className="size-4" aria-hidden />,
              ...blocked(!ready, notReady),
              onSelect: () => open({ kind: "convert", image }),
            },
      ],
      [
        {
          label: t("images.delete"),
          icon: <Trash2 className="size-4" aria-hidden />,
          danger: true,
          ...blocked(
            image.protection.delete || image.status === "creating",
            image.protection.delete ? t("images.why.protected") : notReady,
          ),
          onSelect: () => open({ kind: "delete", image }),
        },
      ],
    ];
  };
}

function EditDialog({
  projectId,
  image,
  onClose,
}: {
  projectId: string;
  image: SavedImage;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateImage(projectId);
  const toast = useToast();
  const [description, setDescription] = useState(image.description);
  const [labels, setLabels] = useState(formatLabels(image.labels));
  const parsed = parseLabels(labels);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!parsed.labels) return;
    update.mutate(
      { id: image.id, description: description.trim(), labels: parsed.labels },
      {
        onSuccess: () => {
          toast({ tone: "ok", title: t("images.saved") });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={t("images.editTitle")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="edit-image"
            busy={update.isPending}
            disabled={parsed.labels === null}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="edit-image" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("images.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={255}
          hint={t("images.descriptionHint")}
        />
        <TextArea
          label={t("images.labels")}
          value={labels}
          onChange={(e) => setLabels(e.target.value)}
          spellCheck={false}
          hint={t("servers.labels.hint")}
          error={
            parsed.badLine === null
              ? undefined
              : t("images.labelsInvalid", { line: parsed.badLine })
          }
        />
        <ErrorMessage error={update.error} />
      </form>
    </Dialog>
  );
}

function ServerChoice({
  servers,
  value,
  onChange,
  blocker,
  label,
}: {
  servers: Server[];
  value: string;
  onChange: (id: string) => void;
  blocker: (server: Server) => string | null;
  label: string;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-10 rounded-lg border border-line bg-surface px-3 text-sm"
      >
        <option value="">{t("images.chooseServer")}</option>
        {servers.map((s) => {
          const why = blocker(s);
          return (
            <option key={s.id} value={s.id} disabled={why !== null}>
              {s.name}
              {why ? ` — ${why}` : ""}
            </option>
          );
        })}
      </select>
    </label>
  );
}

/**
 * Restoring replaces everything on the server's disk with the image: the
 * person types the server's name, and the dialog says what stays and what
 * is lost. With `server` given (the server's own page) no other is offered.
 */
function RestoreDialog({
  projectId,
  image,
  servers,
  busyIds,
  server,
  onClose,
  onStarted,
}: {
  projectId: string;
  image: SavedImage;
  servers: Server[];
  busyIds: ReadonlySet<string>;
  server: Server | undefined;
  onClose: () => void;
  onStarted?: ((operationId: string) => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const failed = useFailure();
  const rebuild = useServerAction(projectId);
  const [password, setPassword] = useState<string | null>(null);
  const [open, setOpen] = useState(true);
  const blocker = (s: Server) => restoreBlocker(t, s, image, busyIds.has(s.id));
  const preferred =
    server ?? servers.find((s) => s.id === (image.server?.id ?? image.createdFrom?.id));
  const [chosenId, setChosenId] = useState(
    preferred && blocker(preferred) === null ? preferred.id : "",
  );
  const chosen = server ?? servers.find((s) => s.id === chosenId);
  const why = server ? blocker(server) : null;
  const when = dateTime(image.createdAt, i18n.language);
  return (
    <>
      <DangerDialog
        open={open}
        onOpenChange={(next) => !next && onClose()}
        title={
          chosen
            ? t("images.restoreTitle", { server: chosen.name })
            : t("images.restoreTitleNoServer")
        }
        consequences={[
          t("images.restoreData", { server: chosen?.name ?? "…", date: when }),
          t("images.restoreReboot"),
          t("images.restoreKeep"),
        ]}
        {...(chosen ? { confirmText: chosen.name } : {})}
        disabled={!chosen || why !== null}
        actionLabel={t("images.restoreAction")}
        busy={rebuild.isPending}
        onConfirm={() =>
          chosen &&
          rebuild.mutate(
            { serverId: chosen.id, action: "rebuild", body: { imageId: image.id } },
            {
              onSuccess: (result) => {
                toast({ tone: "info", title: t("images.restoring", { server: chosen.name }) });
                onStarted?.(result.operation.id);
                if (result.rootPassword) {
                  setOpen(false);
                  setPassword(result.rootPassword);
                } else onClose();
              },
              onError: failed,
            },
          )
        }
      >
        <p className="text-muted">
          {t(image.kind === "backup" ? "images.restoreFromBackup" : "images.restoreFromSnapshot", {
            name: image.description,
            date: when,
          })}
        </p>
        {server ? (
          why && <Alert tone="warn">{why}</Alert>
        ) : (
          <ServerChoice
            servers={servers}
            value={chosenId}
            onChange={setChosenId}
            blocker={blocker}
            label={t("images.restoreServer")}
          />
        )}
        <p className="text-xs text-muted">{t("images.restoreTip")}</p>
      </DangerDialog>
      <RootPasswordDialog password={password} onClose={onClose} />
    </>
  );
}

function ConvertDialog({
  projectId,
  image,
  prices,
  onClose,
}: {
  projectId: string;
  image: SavedImage;
  prices: PriceList | undefined;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const convert = useConvertImage(projectId);
  const toast = useToast();
  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={t("images.convertTitle")}
      description={t("images.convertLead", { name: image.description })}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            busy={convert.isPending}
            onClick={() =>
              convert.mutate(image.id, {
                onSuccess: () => {
                  toast({ tone: "ok", title: t("images.converted", { name: image.description }) });
                  onClose();
                },
              })
            }
          >
            {t("images.convert")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <ul className="flex list-disc flex-col gap-1 pl-5">
          <li>{t("images.convertKeeps")}</li>
          <li>{t("images.convertCount")}</li>
          <li>
            {t("images.convertPrice", { price: snapshotPrice(image, prices, i18n.language) })}
          </li>
        </ul>
        <ErrorMessage error={convert.error} />
      </div>
    </Dialog>
  );
}

/** Takes a snapshot of a chosen server (or the given one). */
function SnapshotDialog({
  projectId,
  servers,
  busyIds,
  serverId,
  prices,
  onClose,
  onStarted,
}: {
  projectId: string;
  servers: Server[];
  busyIds: ReadonlySet<string>;
  serverId: string | null;
  prices: PriceList | undefined;
  onClose: () => void;
  onStarted?: ((operationId: string) => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const failed = useFailure();
  const take = useServerAction(projectId);
  const [chosenId, setChosenId] = useState(serverId ?? "");
  const [description, setDescription] = useState("");
  const chosen = servers.find((s) => s.id === chosenId);
  const blocker = (s: Server) => (busyIds.has(s.id) || s.locked ? t("servers.busy") : null);
  const perGb = prices
    ? money(prices.snapshotPerGbMonth.net, prices.currency, i18n.language, 4)
    : null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!chosen) return;
    take.mutate(
      {
        serverId: chosen.id,
        action: "create-snapshot",
        body: { description: description.trim() },
      },
      {
        onSuccess: (result) => {
          toast({ tone: "info", title: t("images.taking", { server: chosen.name }) });
          onStarted?.(result.operation.id);
          onClose();
        },
        onError: failed,
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={t("images.snapshotTitle")}
      description={t("images.snapshotLead")}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="take-snapshot"
            busy={take.isPending}
            disabled={!chosen || blocker(chosen) !== null}
            icon={<Camera className="size-4" aria-hidden />}
          >
            {t("images.snapshotCreate")}
          </Button>
        </>
      }
    >
      <form id="take-snapshot" onSubmit={submit} className="flex flex-col gap-4">
        {serverId === null && (
          <ServerChoice
            servers={servers}
            value={chosenId}
            onChange={setChosenId}
            blocker={blocker}
            label={t("images.snapshotServer")}
          />
        )}
        <TextField
          label={t("images.description")}
          name="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={255}
          placeholder={t("images.descriptionPlaceholder")}
          hint={t("images.descriptionHint")}
        />
        {chosen?.status === "running" && (
          <Alert tone="neutral">{t("images.snapshotRunning")}</Alert>
        )}
        {perGb && (
          <p className="text-xs text-muted">{t("images.snapshotPrice", { price: perGb })}</p>
        )}
      </form>
    </Dialog>
  );
}

/** Every dialog an image row can open; `server` pins restores and snapshots to one server. */
export function ImageDialogs({
  projectId,
  dialog,
  servers,
  busyIds,
  prices,
  server,
  onClose,
  onStarted,
}: {
  projectId: string;
  dialog: ImageDialog;
  servers: Server[];
  /** Servers with a change running: nothing else can start on them. */
  busyIds: ReadonlySet<string>;
  prices: PriceList | undefined;
  server?: Server;
  onClose: () => void;
  onStarted?: (operationId: string) => void;
}) {
  const { t } = useTranslation();
  const remove = useDeleteImage(projectId);
  const toast = useToast();
  const failed = useFailure();
  const deleting = dialog?.kind === "delete" ? dialog.image : null;
  return (
    <>
      {dialog?.kind === "edit" && (
        <EditDialog projectId={projectId} image={dialog.image} onClose={onClose} />
      )}
      {dialog?.kind === "restore" && (
        <RestoreDialog
          projectId={projectId}
          image={dialog.image}
          servers={servers}
          busyIds={busyIds}
          server={server}
          onClose={onClose}
          onStarted={onStarted}
        />
      )}
      {dialog?.kind === "convert" && (
        <ConvertDialog
          projectId={projectId}
          image={dialog.image}
          prices={prices}
          onClose={onClose}
        />
      )}
      {dialog?.kind === "snapshot" && (
        <SnapshotDialog
          projectId={projectId}
          servers={servers}
          busyIds={busyIds}
          serverId={dialog.serverId}
          prices={prices}
          onClose={onClose}
          onStarted={onStarted}
        />
      )}
      <DangerDialog
        open={deleting !== null}
        onOpenChange={(next) => !next && onClose()}
        title={t("images.deleteTitle", { name: deleting?.description ?? "" })}
        consequences={
          deleting?.kind === "backup"
            ? [t("images.deleteForever"), t("images.deleteBackupOthers")]
            : [t("images.deleteForever"), t("images.deleteSnapshotBilling")]
        }
        actionLabel={t("images.delete")}
        busy={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast({ tone: "ok", title: t("images.deleted", { name: deleting.description }) });
              onClose();
            },
            onError: failed,
          })
        }
      />
    </>
  );
}
